import React, { useState } from 'react';
import { X, Cpu, HardDrive, MemoryStick, Thermometer, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './button';
import { mergeGatewayCompute } from '../../utils/gatewayCompute';
import type { DeviceStats } from '../../types';

interface Props {
  gateway: DeviceStats | null;
  open: boolean;
  onClose: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatBytes = (bytes?: number): string => {
  if (bytes == null || bytes === 0) return '—';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const getUsageColor = (percent: number): string => {
  if (percent >= 90) return '#ef4444';
  if (percent >= 70) return '#f59e0b';
  return '#22c55e';
};

// ─── Shared UI helpers ────────────────────────────────────────────────────────

const SectionHeader = ({ title, accent = '#22c55e' }: { title: string; accent?: string }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="h-4 w-0.5 rounded-full" style={{ backgroundColor: accent }} />
    <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#9AA6B2' }}>
      {title}
    </span>
    <div className="flex-1 h-px" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />
  </div>
);

const StatCard = ({
  label,
  value,
  valueColor,
  className = '',
}: {
  label: string;
  value: React.ReactNode;
  valueColor: string;
  className?: string;
}) => (
  <div
    className={`p-4 rounded-xl flex flex-col gap-2 ${className}`}
    style={{
      backgroundColor: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.05)',
      minHeight: '80px',
    }}
  >
    <span className="text-xs font-medium uppercase tracking-wide" style={{ color: '#8B98A5' }}>
      {label}
    </span>
    <div className="text-2xl font-semibold" style={{ color: valueColor }}>
      {value}
    </div>
  </div>
);

interface ComputeBarProps {
  icon: React.ReactNode;
  label: string;
  percent?: number;
  used?: number;
  total?: number;
}

const ComputeBar: React.FC<ComputeBarProps> = ({ icon, label, percent, used, total }) => {
  const value = percent ?? 0;
  const color = getUsageColor(value);
  return (
    <div
      className="p-4 rounded-xl space-y-2"
      style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span style={{ color }}>{icon}</span>
          <span className="text-xs font-medium uppercase tracking-wide" style={{ color: '#8B98A5' }}>
            {label}
          </span>
        </div>
        <span className="text-2xl font-semibold" style={{ color }}>
          {percent != null ? `${percent.toFixed(1)}%` : '—'}
        </span>
      </div>
      <div className="w-full rounded-full h-1.5" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
        <div
          className="h-1.5 rounded-full transition-all duration-500"
          style={{ width: `${Math.min(value, 100)}%`, backgroundColor: color }}
        />
      </div>
      {(used != null || total != null) && (
        <div className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
          {formatBytes(used)} / {formatBytes(total)}
        </div>
      )}
    </div>
  );
};

// ─── Modal ────────────────────────────────────────────────────────────────────

export const GatewayMetricsSheet: React.FC<Props> = ({ gateway, open, onClose }) => {
  const [showPendingTasks, setShowPendingTasks] = useState(false);
  const [showFailedTasks, setShowFailedTasks] = useState(false);
  const [showCathodeFailedTasks, setShowCathodeFailedTasks] = useState(false);

  if (!open || !gateway) return null;

  const stats = gateway.stats;
  const compute = mergeGatewayCompute(stats as Record<string, unknown>);

  const systemStatusColor = (() => {
    const s = (stats.system_status || '').toLowerCase();
    if (['active', 'online', 'healthy', 'running', 'ok'].some(v => s.includes(v))) return '#22c55e';
    if (['offline', 'down', 'inactive', 'error'].some(v => s.includes(v))) return '#ef4444';
    return '#eab308';
  })();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div
        className="rounded-2xl max-w-4xl w-full flex flex-col"
        style={{
          background: 'linear-gradient(160deg, #0f1720 0%, #121826 100%)',
          border: '1px solid rgba(255,255,255,0.06)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
          maxHeight: '90vh',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-7 py-5 sticky top-0 z-10 rounded-t-2xl"
          style={{
            background: 'linear-gradient(160deg, #0f1720 0%, #121826 100%)',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <div>
            <div className="text-base font-semibold" style={{ color: '#E6EDF3' }}>
              Event Details
            </div>
            <div className="text-sm mt-0.5" style={{ color: '#8B98A5' }}>
              {gateway.client_id} / {gateway.site_id}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="border-0 h-9 w-9 p-0 rounded-xl"
            style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)' }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="px-7 py-6 space-y-8">

            {/* ── Task Statistics ── */}
            <div>
              <SectionHeader title="Task Statistics" accent="#22c55e" />
              <div className="grid grid-cols-3 gap-4">
                <div
                  className="p-4 rounded-xl col-span-1 cursor-pointer select-none transition-all flex flex-col gap-2"
                  style={{
                    backgroundColor: showPendingTasks ? 'rgba(234,179,8,0.08)' : 'rgba(255,255,255,0.03)',
                    border: showPendingTasks ? '1px solid rgba(234,179,8,0.35)' : '1px solid rgba(255,255,255,0.05)',
                    minHeight: '80px',
                  }}
                  onClick={() => setShowPendingTasks(v => !v)}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium uppercase tracking-wide" style={{ color: '#8B98A5' }}>
                      Tasks Pending
                    </span>
                    {showPendingTasks
                      ? <ChevronUp className="h-3.5 w-3.5" style={{ color: '#eab308' }} />
                      : <ChevronDown className="h-3.5 w-3.5" style={{ color: '#eab308' }} />}
                  </div>
                  <div className="text-2xl font-semibold" style={{ color: '#eab308' }}>
                    {stats.tasks_pending_current ?? stats.tasks_pending ?? 0}
                  </div>
                </div>

                {/* Tasks Failed — expandable */}
                <div
                  className="p-4 rounded-xl col-span-1 cursor-pointer select-none transition-all flex flex-col gap-2"
                  style={{
                    backgroundColor: showFailedTasks ? 'rgba(239,68,68,0.08)' : 'rgba(255,255,255,0.03)',
                    border: showFailedTasks ? '1px solid rgba(239,68,68,0.35)' : '1px solid rgba(255,255,255,0.05)',
                    minHeight: '80px',
                  }}
                  onClick={() => setShowFailedTasks(v => !v)}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium uppercase tracking-wide" style={{ color: '#8B98A5' }}>
                      Tasks Failed
                    </span>
                    {showFailedTasks
                      ? <ChevronUp className="h-3.5 w-3.5" style={{ color: '#ef4444' }} />
                      : <ChevronDown className="h-3.5 w-3.5" style={{ color: '#ef4444' }} />}
                  </div>
                  <div className="text-2xl font-semibold" style={{ color: '#ef4444' }}>
                    {stats.tasks_failed_list?.length ?? 0}
                  </div>
                </div>

                {/* Cathode Tasks Failed — expandable */}
                <div
                  className="p-4 rounded-xl col-span-1 cursor-pointer select-none transition-all flex flex-col gap-2"
                  style={{
                    backgroundColor: showCathodeFailedTasks ? 'rgba(239,68,68,0.08)' : 'rgba(255,255,255,0.03)',
                    border: showCathodeFailedTasks ? '1px solid rgba(239,68,68,0.35)' : '1px solid rgba(255,255,255,0.05)',
                    minHeight: '80px',
                  }}
                  onClick={() => setShowCathodeFailedTasks(v => !v)}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium uppercase tracking-wide" style={{ color: '#8B98A5' }}>
                      Cathode Tasks Failed
                    </span>
                    {showCathodeFailedTasks
                      ? <ChevronUp className="h-3.5 w-3.5" style={{ color: '#ef4444' }} />
                      : <ChevronDown className="h-3.5 w-3.5" style={{ color: '#ef4444' }} />}
                  </div>
                  <div className="text-2xl font-semibold" style={{ color: '#ef4444' }}>
                    {(stats as any).cathode_tasks_failed_list?.length ?? 0}
                  </div>
                </div>

                <StatCard
                  label="Total Scans"
                  value={stats.total_scans_current ?? 0}
                  valueColor="#E6EDF3"
                />
                <StatCard
                  label="Failed Uploads"
                  value={
                    stats.images_failed_current ??
                    stats.images_failed ??
                    (stats.tasks as { failed_count?: number } | undefined)?.failed_count ??
                    stats.images_sync_pending_current ??
                    stats.image_sync_pending ??
                    0
                  }
                  valueColor="#eab308"
                />
                <StatCard
                  label="Scans Delivered last 12 hrs"
                  value={stats.images_synced_current ?? 0}
                  valueColor="#22c55e"
                  className="pt-6"
                />
                <StatCard
                  label="System Status"
                  value={<span className="text-lg font-semibold capitalize">{stats.system_status || 'Unknown'}</span>}
                  valueColor={systemStatusColor}
                  className="pt-6"
                />

                {/* Success Rate card */}
                {(() => {
                  const delivered = stats.images_synced_current ?? 0;
                  const total = stats.total_scans_current ?? 0;
                  const hasData = total > 0;
                  const rate = hasData ? (delivered / total) * 100 : 0;
                  const color = hasData ? (rate >= 80 ? '#22c55e' : rate >= 60 ? '#f59e0b' : '#ef4444') : '#64748b';
                  return (
                    <div
                      className="p-4 rounded-xl space-y-2"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.05)',
                        minHeight: '80px',
                      }}
                    >
                      <span className="text-xs font-medium uppercase tracking-wide" style={{ color: '#8B98A5' }}>
                        Success Rate
                      </span>
                      <div className="text-2xl font-semibold" style={{ color }}>
                        {hasData ? `${rate.toFixed(1)}%` : 'N/A'}
                      </div>
                      {hasData && (
                        <>
                          <div className="w-full rounded-full h-1.5" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
                            <div className="h-1.5 rounded-full transition-all duration-500" style={{ width: `${rate}%`, backgroundColor: color }} />
                          </div>
                          <div className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
                            {delivered} of {total} scans
                          </div>
                        </>
                      )}
                      {!hasData && (
                        <div className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
                          No scans in the last 12 hours
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* ── Pending Tasks Detail ── */}
            {showPendingTasks && (
              <div>
                <SectionHeader
                  title={`Pending Tasks${stats.pending_tasks && stats.pending_tasks.length > 0 ? ` (${stats.pending_tasks.length})` : ''}`}
                  accent="#eab308"
                />
                {stats.pending_tasks && stats.pending_tasks.length > 0 ? (
                  <>
                    {/* ── Task Type Breakdown ── */}
                    {(() => {
                      const counts: Record<string, number> = {};
                      for (const task of stats.pending_tasks) {
                        counts[task.type] = (counts[task.type] || 0) + 1;
                      }
                      const rows = Object.entries(counts).sort((a, b) => b[1] - a[1]);
                      return (
                        <div className="rounded-xl overflow-hidden mb-3" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                          <table className="w-full text-xs">
                            <thead>
                              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                                <th className="px-4 py-2 text-left font-medium uppercase tracking-wide" style={{ color: '#9AA6B2' }}>Task Type</th>
                                <th className="px-4 py-2 text-left font-medium uppercase tracking-wide" style={{ color: '#9AA6B2' }}>Count (status 0)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map(([type, count]) => (
                                <tr key={type} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                  <td className="px-4 py-2 text-left font-mono" style={{ color: '#cbd5e1' }}>{type}</td>
                                  <td className="px-4 py-2 text-left font-mono" style={{ color: '#eab308' }}>{count.toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {stats.pending_tasks_truncated && (
                            <div className="px-4 py-2 text-xs" style={{ color: '#64748b', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                              Breakdown based on first 2,000 tasks — {((stats.tasks_pending_current ?? stats.tasks_pending ?? 0) - 2000).toLocaleString()} additional tasks not reflected.
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  <div
                    className="rounded-xl overflow-hidden"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.02)',
                      border: '1px solid rgba(255,255,255,0.05)',
                    }}
                  >
                    {stats.pending_tasks_truncated && (
                      <div
                        className="px-4 py-2 text-xs flex items-center gap-2"
                        style={{
                          backgroundColor: 'rgba(234,179,8,0.08)',
                          borderBottom: '1px solid rgba(234,179,8,0.2)',
                          color: '#eab308',
                        }}
                      >
                        <span>⚠</span>
                        <span>
                          Showing first 2,000 tasks. The actual pending count is{' '}
                          <strong>{(stats.tasks_pending_current ?? stats.tasks_pending ?? 0).toLocaleString()}</strong>.
                        </span>
                      </div>
                    )}
                    <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                      <table className="w-full text-sm">
                        <thead className="sticky top-0" style={{ backgroundColor: 'rgba(15,23,32,0.95)' }}>
                          <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            {(['ID', 'Graph ID', 'Type'] as const).map(h => (
                              <th key={h} className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wide" style={{ color: '#9AA6B2' }}>{h}</th>
                            ))}
                            <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wide" style={{ color: '#9AA6B2' }}>Priority</th>
                            <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wide" style={{ color: '#9AA6B2' }}>Created At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stats.pending_tasks.map((task, i) => (
                            <tr
                              key={i}
                              className="transition-colors"
                              style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                              onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)')}
                              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                              <td className="px-4 py-3 text-left font-mono" style={{ color: '#8B98A5' }}>{task.id}</td>
                              <td className="px-4 py-3 text-left" style={{ color: '#E6EDF3' }}>{task.graph_id}</td>
                              <td className="px-4 py-3 text-left max-w-xs truncate" title={task.type} style={{ color: '#cbd5e1' }}>{task.type}</td>
                              <td className="px-4 py-3 text-left" style={{ color: '#E6EDF3' }}>{task.priority}</td>
                              <td className="px-4 py-3 text-left font-mono text-xs" style={{ color: '#8B98A5' }}>
                                {new Date(task.created_at).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  </>
                ) : (
                  <div
                    className="p-4 rounded-xl text-center"
                    style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
                  >
                    <span className="text-sm" style={{ color: '#64748b' }}>
                      {(stats.tasks_pending_current ?? stats.tasks_pending ?? 0) > 0
                        ? 'Task detail not available from this gateway version'
                        : 'No pending tasks'}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* ── Failed Tasks Detail ── */}
            {showFailedTasks && (
              <div>
                <SectionHeader
                  title={`Failed Tasks${stats.tasks_failed_list && stats.tasks_failed_list.length > 0 ? ` (${stats.tasks_failed_list.length})` : ''}`}
                  accent="#ef4444"
                />
                {stats.tasks_failed_list && stats.tasks_failed_list.length > 0 ? (
                  <div
                    className="rounded-xl overflow-hidden"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.02)',
                      border: '1px solid rgba(255,255,255,0.05)',
                    }}
                  >
                    <div
                      className="px-4 py-2 text-xs flex items-center gap-2"
                      style={{
                        backgroundColor: 'rgba(239,68,68,0.06)',
                        borderBottom: '1px solid rgba(239,68,68,0.15)',
                        color: '#ef4444',
                      }}
                    >
                      <span>Showing most recent {stats.tasks_failed_list.length} failed tasks</span>
                    </div>
                    <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                      <table className="w-full text-sm">
                        <thead className="sticky top-0" style={{ backgroundColor: 'rgba(15,23,32,0.95)' }}>
                          <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wide" style={{ color: '#9AA6B2' }}>ID</th>
                            <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wide" style={{ color: '#9AA6B2' }}>Type</th>
                            <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wide" style={{ color: '#9AA6B2' }}>Exception Type</th>
                            <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wide" style={{ color: '#9AA6B2' }}>Created At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stats.tasks_failed_list.map((task, i) => (
                            <tr
                              key={i}
                              className="transition-colors"
                              style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                              onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)')}
                              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                              <td className="px-4 py-3 text-left font-mono" style={{ color: '#8B98A5' }}>{task.id}</td>
                              <td className="px-4 py-3 text-left max-w-xs truncate" title={task.type} style={{ color: '#cbd5e1' }}>{task.type}</td>
                              <td className="px-4 py-3 text-left font-mono text-xs" style={{ color: '#ef4444' }}>
                                {task.exception_type ?? <span style={{ color: '#64748b' }}>—</span>}
                              </td>
                              <td className="px-4 py-3 text-left font-mono text-xs" style={{ color: '#8B98A5' }}>
                                {new Date(task.created_at).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div
                    className="p-4 rounded-xl text-center"
                    style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
                  >
                    <span className="text-sm" style={{ color: '#64748b' }}>No failed tasks</span>
                  </div>
                )}
              </div>
            )}

            {/* ── Cathode Failed Tasks Detail ── */}
            {showCathodeFailedTasks && (
              <div>
                <SectionHeader
                  title={`Cathode Failed Tasks${(stats as any).cathode_tasks_failed_list?.length > 0 ? ` (${(stats as any).cathode_tasks_failed_list.length})` : ''}`}
                  accent="#ef4444"
                />
                {(stats as any).cathode_tasks_failed_list && (stats as any).cathode_tasks_failed_list.length > 0 ? (
                  <div
                    className="rounded-xl overflow-hidden"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.02)',
                      border: '1px solid rgba(255,255,255,0.05)',
                    }}
                  >
                    <div
                      className="px-4 py-2 text-xs flex items-center gap-2"
                      style={{
                        backgroundColor: 'rgba(239,68,68,0.06)',
                        borderBottom: '1px solid rgba(239,68,68,0.15)',
                        color: '#ef4444',
                      }}
                    >
                      <span>Showing most recent {(stats as any).cathode_tasks_failed_list.length} cathode failed tasks</span>
                    </div>
                    <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                      <table className="w-full text-sm">
                        <thead className="sticky top-0" style={{ backgroundColor: 'rgba(15,23,32,0.95)' }}>
                          <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wide" style={{ color: '#9AA6B2' }}>ID</th>
                            <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wide" style={{ color: '#9AA6B2' }}>Type</th>
                            <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wide" style={{ color: '#9AA6B2' }}>Exception Type</th>
                            <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wide" style={{ color: '#9AA6B2' }}>Created At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(stats as any).cathode_tasks_failed_list.map((task: any, i: number) => (
                            <tr
                              key={i}
                              className="transition-colors"
                              style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                              onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)')}
                              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                              <td className="px-4 py-3 text-left font-mono" style={{ color: '#8B98A5' }}>{task.id}</td>
                              <td className="px-4 py-3 text-left max-w-xs truncate" title={task.type} style={{ color: '#cbd5e1' }}>{task.type}</td>
                              <td className="px-4 py-3 text-left font-mono text-xs" style={{ color: '#ef4444' }}>
                                {task.exception_type ?? <span style={{ color: '#64748b' }}>—</span>}
                              </td>
                              <td className="px-4 py-3 text-left font-mono text-xs" style={{ color: '#8B98A5' }}>
                                {new Date(task.created_at).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div
                    className="p-4 rounded-xl text-center"
                    style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
                  >
                    <span className="text-sm" style={{ color: '#64748b' }}>No cathode failed tasks</span>
                  </div>
                )}
              </div>
            )}

            {/* ── Compute Metrics ── */}
            {(compute.cpu || compute.memory || compute.disk) && (
              <div>
                <SectionHeader title="Compute Metrics" accent="#38bdf8" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <ComputeBar
                    icon={<Cpu className="h-4 w-4" />}
                    label="CPU"
                    percent={compute.cpu?.usage_percent}
                  />
                  <ComputeBar
                    icon={<MemoryStick className="h-4 w-4" />}
                    label="Memory"
                    percent={compute.memory?.usage_percent}
                    used={compute.memory?.used_bytes}
                    total={compute.memory?.total_bytes}
                  />
                  <ComputeBar
                    icon={<HardDrive className="h-4 w-4" />}
                    label="Disk"
                    percent={compute.disk?.usage_percent}
                    used={compute.disk?.used_bytes}
                    total={compute.disk?.total_bytes}
                  />
                </div>
              </div>
            )}

            {/* ── Temperature ── */}
            {compute.temperature && (
              <div>
                <SectionHeader title="Temperature" accent="#f59e0b" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {compute.temperature.cpu_celsius != null && (
                    <div
                      className="p-4 rounded-xl flex items-center gap-4"
                      style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}
                    >
                      <Thermometer
                        className="h-6 w-6"
                        style={{
                          color: compute.temperature.cpu_celsius >= 80
                            ? '#ef4444'
                            : compute.temperature.cpu_celsius >= 65
                              ? '#f59e0b'
                              : '#22c55e',
                        }}
                      />
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide mb-1" style={{ color: '#8B98A5' }}>
                          CPU Temperature
                        </div>
                        <div
                          className="text-2xl font-semibold"
                          style={{
                            color: compute.temperature.cpu_celsius >= 80
                              ? '#ef4444'
                              : compute.temperature.cpu_celsius >= 65
                                ? '#f59e0b'
                                : '#22c55e',
                          }}
                        >
                          {compute.temperature.cpu_celsius.toFixed(1)} °C
                        </div>
                      </div>
                    </div>
                  )}
                  {compute.temperature.sensors && compute.temperature.sensors.length > 0 && (
                    <div
                      className="p-4 rounded-xl"
                      style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}
                    >
                      <div className="text-xs font-medium uppercase tracking-wide mb-3" style={{ color: '#8B98A5' }}>
                        Sensors
                      </div>
                      <div className="space-y-1.5">
                        {compute.temperature.sensors.map((s, i) => (
                          <div key={i} className="flex justify-between text-sm">
                            <span style={{ color: '#cbd5e1' }}>{s.name}</span>
                            <span
                              style={{
                                color: s.temperature >= 80
                                  ? '#ef4444'
                                  : s.temperature >= 65
                                    ? '#f59e0b'
                                    : '#22c55e',
                              }}
                            >
                              {s.temperature.toFixed(1)} °C
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Processes ── */}
            {compute.process && (
              <div>
                <SectionHeader title="Processes" accent="#22c55e" />
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <StatCard label="Total" value={compute.process.total_count ?? '—'} valueColor="#E6EDF3" />
                  <StatCard label="Running" value={compute.process.running_count ?? '—'} valueColor="#22c55e" />
                  <StatCard label="Sleeping" value={compute.process.sleeping_count ?? '—'} valueColor="#8B98A5" />
                </div>

                {compute.process.monitored_processes && compute.process.monitored_processes.length > 0 && (
                  <div
                    className="rounded-xl overflow-hidden"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.02)',
                      border: '1px solid rgba(255,255,255,0.05)',
                    }}
                  >
                    <div
                      className="px-4 py-3 text-xs font-medium uppercase tracking-wide"
                      style={{ color: '#9AA6B2', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
                    >
                      Monitored Processes ({compute.process.monitored_processes.length})
                    </div>
                    <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                      <table className="w-full text-sm">
                        <thead className="sticky top-0" style={{ backgroundColor: 'rgba(15,23,32,0.95)' }}>
                          <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wide" style={{ color: '#9AA6B2' }}>Name</th>
                            <th className="px-4 py-3 text-center font-medium text-xs uppercase tracking-wide" style={{ color: '#9AA6B2' }}>PID</th>
                            <th className="px-4 py-3 text-center font-medium text-xs uppercase tracking-wide" style={{ color: '#9AA6B2' }}>Status</th>
                            <th className="px-4 py-3 text-right font-medium text-xs uppercase tracking-wide" style={{ color: '#9AA6B2' }}>CPU %</th>
                            <th className="px-4 py-3 text-right font-medium text-xs uppercase tracking-wide" style={{ color: '#9AA6B2' }}>Mem %</th>
                            <th className="px-4 py-3 text-right font-medium text-xs uppercase tracking-wide" style={{ color: '#9AA6B2' }}>Mem MB</th>
                          </tr>
                        </thead>
                        <tbody>
                          {compute.process.monitored_processes.map((p, i) => (
                            <tr
                              key={i}
                              className="transition-colors"
                              style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                              onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)')}
                              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                              <td className="px-4 py-3 text-left" style={{ color: '#E6EDF3' }}>{p.name}</td>
                              <td className="px-4 py-3 text-center font-mono text-xs" style={{ color: '#8B98A5' }}>{p.pid}</td>
                              <td className="px-4 py-3 text-center" style={{ color: p.status === 'running' ? '#22c55e' : '#8B98A5' }}>
                                {p.status}
                              </td>
                              <td className="px-4 py-3 text-right tabular-nums" style={{ color: '#E6EDF3' }}>{(p.cpu_percent ?? 0).toFixed(1)}%</td>
                              <td className="px-4 py-3 text-right tabular-nums" style={{ color: '#E6EDF3' }}>{(p.memory_percent ?? 0).toFixed(1)}%</td>
                              <td className="px-4 py-3 text-right tabular-nums" style={{ color: '#E6EDF3' }}>{p.memory_mb} MB</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};
